/**
 * delays invoking func until wait ms after the last call
 * @param func the function to debounce
 * @param wait delay in milliseconds
 * @param immediate whether to invoke on the leading edge
 */
export const debounce = <P extends any[]>(
  func: (...args: P) => void,
  wait: number,
  immediate = false
) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: P | null = null;
  let lastThis: any = null;

  const invoke = () => {
    const args = lastArgs!;
    const that = lastThis;
    lastArgs = null;
    lastThis = null;
    timeout = null;

    func.apply(that, args);
  };

  const debounced = function (this: any, ...args: P) {
    const callNow = immediate && !timeout;

    lastArgs = args;
    lastThis = this;

    if (timeout !== null) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      timeout = null;
      if (!immediate) {
        invoke();
      }
    }, wait);

    if (callNow) {
      invoke();
    }
  };

  debounced.now = function (this: any, ...args: P) {
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }

    lastArgs = args;
    lastThis = this;
    invoke();
  };

  debounced.abort = function () {
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }

    lastArgs = null;
    lastThis = null;
  };

  return debounced;
};

/**
 * limits func to at most one call per wait ms
 * @param func the function to throttle
 * @param wait minimum interval in milliseconds
 * @param leading whether to invoke on the leading edge
 */
export const throttle = <P extends any[]>(
  func: (...args: P) => void,
  wait: number,
  leading = true
) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: P | null = null;
  let lastCallTime = 0;

  const invoke = () => {
    const args = lastArgs!;
    lastArgs = null;
    timeout = null;

    if (leading) {
      lastCallTime = Date.now();
    }

    func(...args);
  };

  const throttled = (...args: P) => {
    lastArgs = args;

    if (leading) {
      const now = Date.now();
      const timeSinceLastCall = now - lastCallTime;

      if (timeSinceLastCall >= wait) {
        if (timeout !== null) {
          clearTimeout(timeout);
        }

        invoke();
      } else if (timeout === null) {
        timeout = setTimeout(invoke, wait - timeSinceLastCall);
      }
    } else {
      if (timeout === null) {
        timeout = setTimeout(invoke, wait);
      }
    }
  };

  throttled.now = (...args: P) => {
    if (timeout !== null) {
      clearTimeout(timeout);
    }

    lastArgs = args;
    invoke();
  };

  throttled.abort = () => {
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return throttled;
};
